import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Checkbox, DatePicker, Input, message } from "antd";
import { checklistApi } from "api/checklist.api";
import { AppButton } from "components/antd-ui";
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ChecklistItem, ChecklistRefType } from "types/checklist";
import dayjs from "utils/dayjs";
import "./Checklist.scss";

const MAX_ITEMS = 50;

export interface ChecklistHandle {
  // Bản ghi cha vừa được tạo xong: lưu các việc con đã nhập tạm
  saveFor: (refId: number) => Promise<void>;
}

type ChecklistProps = {
  refType: ChecklistRefType;
  // Chưa có refId (đang tạo bản ghi cha): việc con được giữ tạm, lưu bằng saveFor sau khi cha tạo xong
  refId?: number;
  disabled?: boolean;
  // Gọi sau mỗi thay đổi đã lưu thành công (để cửa sổ cha làm mới danh sách bên ngoài)
  onChanged?: () => void;
};

// Nhãn tiến độ "2/5" dùng ở các bảng danh sách
export const ChecklistProgress = ({ done = 0, total = 0 }: { done?: number; total?: number }) =>
  total > 0 ? (
    <span className={`checklistProgress ${done === total ? "isAllDone" : ""}`}>
      {done}/{total}
    </span>
  ) : null;

const ChecklistRow = ({
  item,
  disabled,
  onToggle,
  onRename,
  onDeadline,
  onRemove,
}: {
  item: ChecklistItem;
  disabled?: boolean;
  onToggle: (id: ChecklistItem["id"]) => void;
  onRename: (id: ChecklistItem["id"], name: string) => void;
  onDeadline: (id: ChecklistItem["id"], deadlineAt: number) => void;
  onRemove: (id: ChecklistItem["id"]) => void;
}) => {
  const [draft, setDraft] = useState(item.name);
  useEffect(() => setDraft(item.name), [item.name]);

  const overdue = !item.done && item.deadlineAt > 0 && dayjs.unix(item.deadlineAt).isBefore(dayjs(), "day");

  if (disabled) {
    return (
      <div className="checklist__row">
        <Checkbox checked={item.done} disabled />
        <span className={`checklist__name ${item.done ? "isDone" : ""}`}>{item.name}</span>
        <span className={`checklist__deadline ${overdue ? "isOverdue" : ""}`}>
          {item.deadlineAt > 0 ? dayjs.unix(item.deadlineAt).format("DD/MM/YYYY") : ""}
          {overdue && " · Quá hạn"}
        </span>
      </div>
    );
  }

  return (
    <div className="checklist__row">
      <Checkbox checked={item.done} onChange={() => onToggle(item.id)} />
      <Input.TextArea
        className={`checklist__input ${item.done ? "isDone" : ""}`}
        variant="borderless"
        autoSize={{ minRows: 1, maxRows: 6 }}
        value={draft}
        maxLength={255}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          const name = draft.trim();
          if (!name) setDraft(item.name);
          else if (name !== item.name) onRename(item.id, name);
        }}
        onPressEnter={(event) => {
          event.preventDefault();
          (event.target as HTMLTextAreaElement).blur();
        }}
      />
      <DatePicker
        className={`checklist__date ${overdue ? "isOverdue" : ""}`}
        format="DD/MM/YYYY"
        placeholder="Thời hạn"
        value={item.deadlineAt > 0 ? dayjs.unix(item.deadlineAt) : null}
        onChange={(date) => onDeadline(item.id, date ? date.startOf("day").unix() : 0)}
      />
      <AppButton
        className="checklist__remove"
        aria-label="Xóa việc con"
        icon={<DeleteOutlined />}
        onClick={() => onRemove(item.id)}
      />
    </div>
  );
};

// Danh sách việc con dùng chung cho nhiều menu (gắn theo refType + refId, giống CommentThread): tự tải, tự lưu từng thao tác
export const Checklist = React.forwardRef<ChecklistHandle, ChecklistProps>(({ refType, refId, disabled, onChanged }, ref) => {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const tempSeq = useRef(0);

  useEffect(() => {
    if (!refId) {
      setItems([]);
      return;
    }
    let ignore = false;
    checklistApi.findAll({ refType, refId }).then((res) => {
      if (!ignore) setItems(res.data.items || []);
    });
    return () => {
      ignore = true;
    };
  }, [refType, refId]);

  useImperativeHandle(
    ref,
    () => ({
      async saveFor(newRefId: number) {
        for (const item of items) {
          await checklistApi.create({
            refType,
            refId: newRefId,
            name: item.name,
            deadlineAt: item.deadlineAt,
            done: item.done,
          });
        }
      },
    }),
    [items, refType],
  );

  const patch = useCallback((id: ChecklistItem["id"], change: Partial<ChecklistItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...change } : item)));
  }, []);

  // Cập nhật một việc con: sửa trên màn hình ngay, lưu lên server nếu đã có bản ghi cha, lỗi thì trả về như cũ
  const save = useCallback(
    async (id: ChecklistItem["id"], change: Partial<ChecklistItem>) => {
      const previous = items.find((item) => item.id === id);
      patch(id, change);
      if (!refId || typeof id !== "number") return;
      try {
        await checklistApi.update(id, change);
        onChanged?.();
      } catch {
        if (previous) patch(id, previous);
      }
    },
    [items, onChanged, patch, refId],
  );

  const handleToggle = useCallback(
    (id: ChecklistItem["id"]) => {
      const current = items.find((item) => item.id === id);
      if (current) save(id, { done: !current.done });
    },
    [items, save],
  );

  const handleRemove = useCallback(
    async (id: ChecklistItem["id"]) => {
      if (refId && typeof id === "number") {
        try {
          await checklistApi.delete(id);
          onChanged?.();
        } catch {
          return;
        }
      }
      setItems((prev) => prev.filter((item) => item.id !== id));
    },
    [onChanged, refId],
  );

  const handleAdd = useCallback(async () => {
    const name = newName.trim();
    if (!name) return;
    if (items.length >= MAX_ITEMS) {
      message.warning(`Tối đa ${MAX_ITEMS} việc con`);
      return;
    }
    if (!refId) {
      tempSeq.current += 1;
      setItems((prev) => [...prev, { id: `tmp-${tempSeq.current}`, name, deadlineAt: 0, done: false }]);
      setNewName("");
      return;
    }
    setAdding(true);
    try {
      const res = await checklistApi.create({ refType, refId, name });
      setItems((prev) => [...prev, res.data as ChecklistItem]);
      setNewName("");
      onChanged?.();
    } finally {
      setAdding(false);
    }
  }, [items.length, newName, onChanged, refId, refType]);

  const done = items.filter((item) => item.done).length;

  return (
    <div className="checklist">
      <div className="checklist__header">
        <h3 className="font-semibold">Danh sách việc con</h3>
        {items.length > 0 && (
          <span className="checklist__progress">
            {done}/{items.length} đã làm
          </span>
        )}
      </div>

      {items.length === 0 && disabled && <div className="text-gray-400 text-sm">Chưa có việc con nào</div>}

      {items.map((item) => (
        <ChecklistRow
          key={item.id}
          item={item}
          disabled={disabled}
          onToggle={handleToggle}
          onRename={(id, name) => save(id, { name })}
          onDeadline={(id, deadlineAt) => save(id, { deadlineAt })}
          onRemove={handleRemove}
        />
      ))}

      {!disabled && (
        <div className="checklist__add">
          <Input
            value={newName}
            maxLength={255}
            placeholder="Thêm việc con, nhấn Enter"
            onChange={(event) => setNewName(event.target.value)}
            onPressEnter={handleAdd}
          />
          <AppButton icon={<PlusOutlined />} loading={adding} onClick={handleAdd}>
            Thêm
          </AppButton>
        </div>
      )}
    </div>
  );
});

Checklist.displayName = "Checklist";
